import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { DatePicker, Table, Tooltip, Select } from "antd";
import "bootstrap-icons/font/bootstrap-icons.css";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { loadDashboardData } from "../../redux/rtk/features/dashboard/dashboardSlice";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";

function CustomTable({ list, dashboardStatus }) {
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [filterStatus, setFilterStatus] = useState(dashboardStatus || "All");
  const { loading } = useSelector((state) => state.dashboard); // Use dashboard slice for loading

  const columns = [
    {
      title: "User Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        const statusIcons = {
          Present: <CheckCircleOutlined style={{ color: "green" }} />,
          "On Leave": <ExclamationCircleOutlined style={{ color: "orange" }} />,
          Absent: <CloseCircleOutlined style={{ color: "red" }} />,
        };
        return statusIcons[text] ? <Tooltip title={text}>{statusIcons[text]}</Tooltip> : null;
      },
    },
  ];

  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  useEffect(() => {
    setFilterStatus(dashboardStatus || "All");
  }, [dashboardStatus]);

  const formatData = (data = {}) => {
    const formattedData = [];
    (data.todayPresentUsers || []).forEach((user) => formattedData.push({ status: "Present", name: user }));
    (data.todayOnLeaveUsers || []).forEach((user) => formattedData.push({ status: "On Leave", name: user }));
    (data.todayAbsentUsers || []).forEach((user) => formattedData.push({ status: "Absent", name: user }));
    return formattedData;
  };

  const filteredData = useMemo(() => {
    if (!list) return [];
    const formattedData = formatData(list);
    return filterStatus === "All" ? formattedData : formattedData.filter((user) => user.status === filterStatus);
  }, [list, filterStatus]);


  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  return (
    <div className="ant-card p-4 rounded mt-5">
      <div className="flex my-2 justify-between">
        <div className="w-50">
          <h4 className="text-2xl mb-2">Attendance List</h4>
        </div>
        <Select
          value={filterStatus}
          style={{ width: 150 }}
          onChange={(value) => {
            setFilterStatus(value);
            const params = new URLSearchParams(window.location.search);
            params.set("dashboardStatus", value);
            window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
          }}
        >
          <Select.Option value="All">All</Select.Option>
          <Select.Option value="Present">Present</Select.Option>
          <Select.Option value="On Leave">On Leave</Select.Option>
          <Select.Option value="Absent">Absent</Select.Option>
        </Select>
      </div>
      {list && (
        <div style={{ marginBottom: "30px" }}>
          <ColVisibilityDropdown
            options={columns}
            columns={columns}
            columnsToShowHandler={columnsToShowHandler}
          />
        </div>
      )}
      <Table
        scroll={{ x: true }}
        loading={loading}
        pagination={{ defaultPageSize: 20 }}
        columns={columnsToShow}
        dataSource={filteredData}
      />
    </div>
  );
}

const GetTotalAttendance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const list = useSelector((state) => state.dashboard.list);

  const startdate = queryParams.get("startdate") || dayjs().format("YYYY-MM-DD");
  const dashboardStatus = queryParams.get("dashboardStatus") || "All";
  const [selectedDate, setSelectedDate] = useState(dayjs(startdate));

  useEffect(() => {
    dispatch(loadDashboardData({ startdate, enddate: startdate }));
  }, [dispatch, startdate]);

  const onDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      const formattedDate = date.format("YYYY-MM-DD");
      const params = new URLSearchParams({
        startdate: formattedDate,
        enddate: formattedDate,
        dashboardStatus,
      });
      navigate(`?${params.toString()}`);
    }
  };

  return (
    <UserPrivateComponent permission="readAll-user">
      <div className="card card-custom">
        <div className="card-body">
          <div className="flex items-center mb-4">
            <DatePicker
              value={selectedDate}
              onChange={onDateChange}
              format="YYYY-MM-DD"
            />
          </div>
          <CustomTable list={list} dashboardStatus={dashboardStatus} />
        </div>
      </div>
    </UserPrivateComponent>
  );
};

export default GetTotalAttendance;